/**
 * Address-sharing helpers used by every product screen.
 *
 * The user only enters the address once; every other selected product reuses it
 * regardless of the order in which the screens are visited.
 *
 * "First selected" / "upstream" are resolved against the CONFIGURED product order
 * (getProductOrder), so reordering products in config.json keeps the capture-once
 * behaviour correct whatever the order is.
 */

import { getProductOrder } from '../workflowConfig';
import { INBOEDEL_INFOFOLIO_FIELDS_KEY, INBOEDEL_INFOFOLIO_LOOKUP_DONE_KEY } from './glassPremium';

export type SaaProductId =
  | 'personenauto'
  | 'woonhuis'
  | 'inboedel'
  | 'aansprakelijkheid'
  | 'rechtsbijstand'
  | 'ongevallen'
  | 'doorlopendereis';

export const SAA_PRODUCT_IDS: readonly SaaProductId[] = [
  'woonhuis',
  'inboedel',
  'aansprakelijkheid',
  'rechtsbijstand',
  'ongevallen',
  'personenauto',
  'doorlopendereis',
];

export interface BAAddress {
  BA_PCODE?: string;
  BA_HUISNR?: string | number;
  BA_TOEVOEG?: string;
  BA_STRAAT?: string;
  BA_PLAATS?: string;
}

const hasMinimalAddress = (ba: any): ba is BAAddress =>
  Boolean(ba && ba.BA_PCODE && ba.BA_HUISNR);

/**
 * The VP (policyholder) address as BA_* fields, for the risicoadres-gelijk 'J' case:
 * only fields the VP actually carries are returned, so merging never blanks an existing
 * BA value with an absent VP one. BA_TOEVOEG deliberately copies '' too (a cleared
 * addition is a real answer). Shared by the Woonhuis and Inboedel toggle handlers so
 * the two property products cannot drift.
 */
export const vpAddressAsBa = (vp: any): Partial<BAAddress> => ({
  ...(vp?.VP_PCODE ? { BA_PCODE: vp.VP_PCODE } : {}),
  ...(vp?.VP_HUISNR ? { BA_HUISNR: vp.VP_HUISNR } : {}),
  ...(vp?.VP_TOEVOEG !== undefined ? { BA_TOEVOEG: vp.VP_TOEVOEG } : {}),
  ...(vp?.VP_STRAAT ? { BA_STRAAT: vp.VP_STRAAT } : {}),
  ...(vp?.VP_PLAATS ? { BA_PLAATS: vp.VP_PLAATS } : {}),
});

/**
 * The policyholder address a product keeps OUTSIDE PP.BA, normalized to BA_* fields.
 * Ongevallen and Doorlopende reis (addressLocation 'VP') persist the full address on
 * Relatiedocument.VP and never write PP.BA; a product synced by
 * {@link syncAddressToOtherProducts} always carries it on Contractdocument.PP.VP.
 * Null when neither holds a complete (postcode + huisnummer) address.
 */
const findVpAddress = (stepData: any): BAAddress | null => {
  for (const vp of [stepData?.Relatiedocument?.VP, stepData?.Contractdocument?.PP?.VP]) {
    const ba = vpAddressAsBa(vp);
    if (hasMinimalAddress(ba)) return ba;
  }
  return null;
};

/**
 * Find a usable address on any other product's step data. Returns the first address
 * with both postcode and huisnummer set — Contractdocument.PP.BA when the product has
 * one, otherwise its VP-located address (see {@link findVpAddress}) — or null if none
 * of the other products has one yet. The current product is excluded so a screen never
 * reads from its own slice via this helper.
 */
export const findUpstreamAddress = (
  getStepData: (id: string) => any,
  currentProductId: SaaProductId,
): BAAddress | null => {
  for (const id of getProductOrder()) {
    if (id === currentProductId) continue;
    const stepData = getStepData(id);
    const ba = stepData?.Contractdocument?.PP?.BA;
    if (hasMinimalAddress(ba)) return ba;
    const fromVp = findVpAddress(stepData);
    if (fromVp) return fromVp;
  }
  return null;
};

/**
 * Whether any *selected* other product already has the address filled in.
 * Kept for backwards compatibility. The product screens now treat the address
 * as editable everywhere (changes propagate via {@link syncAddressToOtherProducts}),
 * so callers no longer use this — but it stays exported in case a screen needs
 * the "address came from elsewhere" signal for, say, an info banner.
 */
export const isAddressLockedByOthers = (
  selectedProducts: Record<string, boolean | undefined> | undefined,
  getStepData: (id: string) => any,
  currentProductId: SaaProductId,
): boolean => {
  if (!selectedProducts) return false;
  for (const id of getProductOrder()) {
    if (id === currentProductId) continue;
    if (!selectedProducts[id]) continue;
    const ba = getStepData(id)?.Contractdocument?.PP?.BA;
    if (hasMinimalAddress(ba)) return true;
  }
  return false;
};

/**
 * Address is captured once on the first selected product (in the configured
 * workflow order from getProductOrder) and propagated forward via
 * syncAddressToOtherProducts. Subsequent product screens hide their
 * address UI so the user can't enter (or worse, diverge) a second copy. Returns
 * true only when this product is not the first selected AND another selected
 * product already has a complete address.
 */
export const shouldHideAddressSection = (
  currentProductId: SaaProductId,
  selectedProducts: Record<string, boolean | undefined> | undefined,
  getStepData: (id: string) => any,
): boolean => {
  if (!selectedProducts) return false;
  let firstSelected: SaaProductId | null = null;
  for (const id of getProductOrder()) {
    if (selectedProducts[id]) {
      firstSelected = id;
      break;
    }
  }
  if (!firstSelected || firstSelected === currentProductId) return false;
  return !!findUpstreamAddress(getStepData, currentProductId);
};

// Building characteristics that describe the insured building itself. Shared between property
// products (woonhuis/inboedel) when they insure the SAME building, so the user enters them
// once whatever the screen order is. Excludes address fields and the product-specific insured-
// value fields (woonhuis herbouwwaarde / inboedel inboedelwaarde live outside this set).
export const SHARED_BUILDING_FIELDS = [
  'BA_TYPEWON', 'BA_WONING', 'BA_OPPWON', 'BA_AARDBW', 'BA_AARDDAK', 'BA_BOUWJR',
  'BA_EIGENWO', 'BA_PERMBEW', 'BA_BDRFBEL', 'BA_MONUMEN', 'BA_BGM2', 'BA_TOPPBGB',
  'BA_DAKCONS', 'BA_DAKBED', 'BA_DAKBED_CODE', 'BA_RIETDAK', 'BA_SRTRDAK', 'BA_ZONNPAN',
  'BA_OPZPKVA',
];

// Whether two BA blocks refer to the same physical building (same risk address). Building
// details may only be reused when this holds — otherwise the two property products insure
// different buildings (divergent risicoadres) and must each keep their own characteristics.
export const isSameBuilding = (a: any, b: any): boolean =>
  Boolean(
    a && b && a.BA_PCODE && a.BA_HUISNR &&
    a.BA_PCODE === b.BA_PCODE &&
    String(a.BA_HUISNR) === String(b.BA_HUISNR) &&
    (a.BA_TOEVOEG || '') === (b.BA_TOEVOEG || ''),
  );

// Return a copy of `target` BA with the shared building characteristics from `source` BA
// merged in (only fields present on source). Used so a property product reuses building
// details already entered on a sibling property product for the same building.
export const copyBuildingDetails = (source: any, target: any): any => {
  const out = { ...(target || {}) };
  for (const f of SHARED_BUILDING_FIELDS) {
    if (source?.[f] !== undefined) out[f] = source[f];
  }
  return out;
};

// BA fields derived from the Infofolio property lookup. When the address changes we strip
// these from each downstream product so its mount effect re-fetches Infofolio for the new
// postcode (otherwise Woonhuis/Inboedel would carry over building data for the old address).
export const INFOFOLIO_DERIVED_BA_FIELDS = [
  'BA_BOUWJR', 'BA_HERBWRD', 'BA_OPPWON', 'BA_TYPEWON',
  'BA_AARDBW', 'BA_AARDDAK', 'BA_MONUMEN', 'BA_WONING', 'BA_EIGENWO',
  'BA_DAKCONS', 'BA_DAKBED', 'BA_DAKBED_CODE', 'BA_BGM2', 'BA_TOPPBGB',
  'BA_HERBVST', 'BA_RIETDAK', 'BA_SRTRDAK', 'BA_ZONNPAN', 'BA_PERMBEW',
  'BA_OPZPKVA',
];

/**
 * Drop the Infofolio-derived BA fields from a product's `_uiState` stash.
 *
 * `cleanInboedelForSchema` mirrors part of PP.BA into `_uiState`, and `applyProductDefaults`
 * restores PP.BA from that stash on the next mount. Clearing PP.BA alone therefore does not
 * survive a remount: the stash re-seeds the previous address's building data, and for
 * BA_HERBWRD that means the Midglas engine rates glass over the wrong rebuild value.
 * Returns undefined when there is no stash, so callers can spread conditionally.
 */
export const stripInfofolioUiState = (uiState: any): any | undefined => {
  if (!uiState) return undefined;
  const next = { ...uiState };
  for (const field of INFOFOLIO_DERIVED_BA_FIELDS) delete next[field];
  return next;
};

// SessionStorage keys written by Woonhuis/Inboedel for their Infofolio caches.
// Cleared when the address changes so downstream screens don't read stale values
// on mount before their own fetchPropertyInfo finishes.
export const INFOFOLIO_SESSION_KEYS = [
  'infofolio_BA_HERBWRD',
  'woonhuis_infofolio_fields',
  'woonhuis_infofolio_address',
  INBOEDEL_INFOFOLIO_FIELDS_KEY,
  'inboedel_infofolio_address',
  INBOEDEL_INFOFOLIO_LOOKUP_DONE_KEY,
];

/**
 * Push an address from one product to every other selected product so the user
 * never has to retype it.
 *
 * - VP (policyholder) is always synced — that's the customer, not the risk.
 * - BA (risk address) is synced for AVP/RBV/Personenauto (single-address products) and
 *   for Inboedel/Woonhuis when their RisicoAdresGelijk toggle is 'J' (default).
 * - Infofolio-derived BA fields are stripped from BA so the downstream screen
 *   re-fetches building data for the new postcode.
 */
export const syncAddressToOtherProducts = (
  sourceProductId: SaaProductId,
  address: BAAddress,
  selectedProducts: Record<string, boolean | undefined> | undefined,
  getStepData: (id: string) => any,
  saveStepData: (id: string, data: any) => void,
): void => {
  if (!selectedProducts) return;
  const huisnrNum = Number(address.BA_HUISNR);
  const vpHuisnr = isNaN(huisnrNum) ? 0 : huisnrNum;
  let anyChanged = false;

  for (const product of getProductOrder()) {
    if (product === sourceProductId) continue;
    if (!selectedProducts[product]) continue;

    const stepData = getStepData(product);
    if (!stepData?.Contractdocument?.PP) continue;

    const productBA = stepData.Contractdocument.PP.BA || {};
    if (
      productBA.BA_PCODE === address.BA_PCODE &&
      String(productBA.BA_HUISNR) === String(address.BA_HUISNR ?? '') &&
      (productBA.BA_TOEVOEG || '') === (address.BA_TOEVOEG || '')
    ) {
      continue;
    }
    anyChanged = true;

    const risicoGelijk = stepData.Supportsection?.Divers?.RisicoAdresGelijk ?? 'J';
    const shouldSyncBA =
      product === 'aansprakelijkheid' ||
      product === 'rechtsbijstand' ||
      product === 'personenauto' ||
      risicoGelijk === 'J';

    const cleanedBA: any = { ...productBA };
    for (const field of INFOFOLIO_DERIVED_BA_FIELDS) delete cleanedBA[field];

    // Force Inboedel to refetch Infofolio when the address changes by clearing the
    // inboedelwaarde fields that gate its fetchPropertyInfo guard.
    const nextIP = product === 'inboedel' && stepData.Contractdocument.PP.IP
      ? (() => { const ip = { ...stepData.Contractdocument.PP.IP }; delete ip.IP_VERZSOM; return ip; })()
      : undefined;
    const nextIB = product === 'inboedel' && stepData.Contractdocument.PP.IB
      ? (() => { const ib = { ...stepData.Contractdocument.PP.IB }; delete ib.IB_INBVST; return ib; })()
      : undefined;

    const cleanedUiState = stripInfofolioUiState(stepData._uiState);

    const updatedDownstream = {
      ...stepData,
      ...(cleanedUiState ? { _uiState: cleanedUiState } : {}),
      Contractdocument: {
        ...stepData.Contractdocument,
        PP: {
          ...stepData.Contractdocument.PP,
          ...(nextIP ? { IP: nextIP } : {}),
          ...(nextIB ? { IB: nextIB } : {}),
          VP: {
            ...(stepData.Contractdocument.PP.VP || {}),
            VP_PCODE: address.BA_PCODE,
            VP_HUISNR: vpHuisnr,
            VP_TOEVOEG: address.BA_TOEVOEG || '',
            VP_STRAAT: address.BA_STRAAT,
            VP_PLAATS: address.BA_PLAATS,
          },
          ...(shouldSyncBA
            ? {
                BA: {
                  ...cleanedBA,
                  BA_PCODE: address.BA_PCODE,
                  BA_HUISNR: address.BA_HUISNR,
                  BA_TOEVOEG: address.BA_TOEVOEG || '',
                  // Keep looked-up street/city on the downstream product so it
                  // doesn't need to fire its own lookup just to render the address.
                  BA_STRAAT: address.BA_STRAAT,
                  BA_PLAATS: address.BA_PLAATS,
                },
              }
            : {}),
        },
      },
    };
    saveStepData(product, updatedDownstream);
  }

  if (anyChanged && typeof window !== 'undefined') {
    for (const key of INFOFOLIO_SESSION_KEYS) sessionStorage.removeItem(key);
  }
};

/**
 * Find a divergent risk address (BA) on any other property product's step data —
 * i.e. a product whose RisicoAdresGelijk is 'N', meaning its BA is a standalone
 * insured-property address rather than a mirror of the policyholder address.
 *
 * Used as a fallback for sessions that predate the shared riskAddress slice, and
 * to detect "a sibling property product already collected the risk address" so
 * the current screen can show a read-only summary instead of an empty block.
 * Returns the first match; null when no sibling has a divergent risk address yet.
 *
 * A sibling that was flipped to 'N' but whose BA still MIRRORS its own policyholder
 * address has not collected a risk address yet — it is the address the user just said
 * is not the risk address. Answering "Nee" on one property product flips the other's
 * toggle too, so without this guard the screen would render that mirrored address as a
 * read-only "already collected" summary, with no input to correct it and its own BA
 * empty (Next blocked on postcode/huisnummer). Such siblings are skipped.
 */
export const findUpstreamRiskAddress = (
  getStepData: (id: string) => any,
  currentProductId: SaaProductId,
): BAAddress | null => {
  for (const id of getProductOrder()) {
    if (id === currentProductId) continue;
    const stepData = getStepData(id);
    if (stepData?.Supportsection?.Divers?.RisicoAdresGelijk !== 'N') continue;
    const ba = stepData?.Contractdocument?.PP?.BA;
    if (!hasMinimalAddress(ba)) continue;
    if (isSameBuilding(ba, vpAddressAsBa(stepData?.Contractdocument?.PP?.VP))) continue;
    return ba;
  }
  return null;
};

/**
 * The sibling's risk address to show as a read-only summary on the current screen.
 * None when the current product collected it itself (riskAddressSource): its own input is
 * pushed to the siblings by {@link syncRiskAddressToProperties} on every keystroke, and
 * {@link findUpstreamRiskAddress} would hand that half-typed copy straight back, replacing
 * the editable block with a summary the user cannot finish.
 */
export const findSiblingRiskAddress = (
  getStepData: (id: string) => any,
  currentProductId: SaaProductId,
  riskAddressSource: SaaProductId | null | undefined,
): BAAddress | null =>
  riskAddressSource === currentProductId ? null : findUpstreamRiskAddress(getStepData, currentProductId);

/**
 * Push a divergent risk address (BA) to every other selected product whose own
 * RisicoAdresGelijk is 'N'. Products on 'J' (and the single-address products
 * AVP/RBV/Personenauto) follow the policyholder address via
 * {@link syncAddressToOtherProducts} and are skipped here. Infofolio-derived BA
 * fields are stripped — and Inboedel's verzekerde-som fields cleared — so the
 * sibling re-fetches building data for the risk postcode.
 */
export const syncRiskAddressToProperties = (
  sourceProductId: SaaProductId,
  riskAddress: BAAddress,
  selectedProducts: Record<string, boolean | undefined> | undefined,
  getStepData: (id: string) => any,
  saveStepData: (id: string, data: any) => void,
): void => {
  if (!selectedProducts) return;
  let anyChanged = false;

  for (const product of getProductOrder()) {
    if (product === sourceProductId) continue;
    if (!selectedProducts[product]) continue;

    const stepData = getStepData(product);
    if (!stepData?.Contractdocument?.PP) continue;
    if ((stepData.Supportsection?.Divers?.RisicoAdresGelijk ?? 'J') !== 'N') continue;

    const productBA = stepData.Contractdocument.PP.BA || {};
    if (
      productBA.BA_PCODE === riskAddress.BA_PCODE &&
      String(productBA.BA_HUISNR) === String(riskAddress.BA_HUISNR ?? '') &&
      (productBA.BA_TOEVOEG || '') === (riskAddress.BA_TOEVOEG || '')
    ) {
      continue;
    }
    anyChanged = true;

    const cleanedBA: any = { ...productBA };
    for (const field of INFOFOLIO_DERIVED_BA_FIELDS) delete cleanedBA[field];

    const nextIP = product === 'inboedel' && stepData.Contractdocument.PP.IP
      ? (() => { const ip = { ...stepData.Contractdocument.PP.IP }; delete ip.IP_VERZSOM; return ip; })()
      : undefined;
    const nextIB = product === 'inboedel' && stepData.Contractdocument.PP.IB
      ? (() => { const ib = { ...stepData.Contractdocument.PP.IB }; delete ib.IB_INBVST; return ib; })()
      : undefined;

    const cleanedUiState = stripInfofolioUiState(stepData._uiState);

    const updated = {
      ...stepData,
      ...(cleanedUiState ? { _uiState: cleanedUiState } : {}),
      Contractdocument: {
        ...stepData.Contractdocument,
        PP: {
          ...stepData.Contractdocument.PP,
          ...(nextIP ? { IP: nextIP } : {}),
          ...(nextIB ? { IB: nextIB } : {}),
          BA: {
            ...cleanedBA,
            BA_PCODE: riskAddress.BA_PCODE,
            BA_HUISNR: riskAddress.BA_HUISNR,
            BA_TOEVOEG: riskAddress.BA_TOEVOEG || '',
            BA_STRAAT: riskAddress.BA_STRAAT,
            BA_PLAATS: riskAddress.BA_PLAATS,
          },
        },
      },
    };
    saveStepData(product, updated);
  }

  if (anyChanged && typeof window !== 'undefined') {
    for (const key of INFOFOLIO_SESSION_KEYS) sessionStorage.removeItem(key);
  }
};
